import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Checkbox } from '~/components/ui/checkbox';

// Per-action "don't show this again" warnings for the inbox chat modal's
// resolve actions. Suppression is stored in localStorage under namespaced
// `eddy.*` keys (matching the codebase convention, e.g. `eddy.inbox.warn.*`
// below). Kept separate from the app-wide `useConfirm` so that simple
// primitive stays free of this opt-out affordance.

export type WarningKey = 'graph' | 'delete';

const LS_KEY: Record<WarningKey, string> = {
  graph: 'eddy.inbox.warn.saveGraph',
  delete: 'eddy.inbox.warn.deleteChat',
};

export function isWarningSuppressed(key: WarningKey): boolean {
  try {
    return localStorage.getItem(LS_KEY[key]) === '1';
  } catch {
    return false;
  }
}

function suppressWarning(key: WarningKey): void {
  try {
    localStorage.setItem(LS_KEY[key], '1');
  } catch {
    /* ignore */
  }
}

interface InboxWarningDialogProps {
  open: boolean;
  warningKey: WarningKey;
  title: string;
  description: string;
  confirmText: string;
  destructive?: boolean;
  /** Called with the final decision. `confirmed` true = proceed. */
  onResolve: (confirmed: boolean) => void;
}

/**
 * A confirm dialog with a "Don't show this again" checkbox. On confirm, if the
 * box is ticked, the matching warning is suppressed for future invocations
 * (the caller checks `isWarningSuppressed` before opening this at all).
 */
export function InboxWarningDialog({
  open,
  warningKey,
  title,
  description,
  confirmText,
  destructive,
  onResolve,
}: InboxWarningDialogProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const finish = (confirmed: boolean) => {
    if (confirmed && dontShowAgain) suppressWarning(warningKey);
    setDontShowAgain(false);
    onResolve(confirmed);
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <Checkbox
            checked={dontShowAgain}
            onCheckedChange={(c) => setDontShowAgain(c === true)}
          />
          Don't show this again
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => finish(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => finish(true)}
            className={destructive ? 'bg-destructive text-white hover:bg-destructive/90' : undefined}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
